export interface AuthClientError {
  code?: string;
  message?: string;
  status?: number;
  statusText?: string;
}

export interface AuthClientResult<T = unknown> {
  data?: T | null;
  error?: AuthClientError | null;
}

export type AuthBanner = { kind: "info" | "success"; text: string };

export interface AuthErrorCopy {
  rateLimited?: string;
}

const EMAIL_NOT_VERIFIED_MESSAGE =
  "Confirm your email before signing in. Use the resend link below if you need a new verification email.";

export function normalizeAuthError(error: unknown, fallback: string, copy: AuthErrorCopy = {}): string {
  const authError = error && typeof error === "object" ? (error as AuthClientError) : null;
  const code = authError?.code?.toUpperCase();
  const message = authError?.message?.trim();
  const lower = message?.toLowerCase() ?? "";

  if (authError?.status === 429) {
    return copy.rateLimited ?? message ?? "Too many requests. Please wait before trying again.";
  }

  if (code === "EMAIL_NOT_VERIFIED" || lower.includes("email not verified")) {
    return EMAIL_NOT_VERIFIED_MESSAGE;
  }

  if (code === "INVALID_EMAIL_OR_PASSWORD" || lower.includes("invalid email or password")) {
    return message || "Invalid email or password";
  }

  if (code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL" || lower.includes("user already exists")) {
    return "An account already exists for this email. Sign in instead, or reset your password.";
  }

  return message || fallback;
}

export function authResultError(result: unknown, fallback: string): string | null {
  const error = (result as AuthClientResult | null | undefined)?.error;
  return error ? normalizeAuthError(error, fallback) : null;
}

export function isEmailVerificationError(error: unknown): boolean {
  const authError = error && typeof error === "object" ? (error as AuthClientError) : null;
  const code = authError?.code?.toUpperCase();
  const message = authError?.message?.toLowerCase() ?? "";
  return code === "EMAIL_NOT_VERIFIED" || message.includes("email not verified");
}

export function resultNeedsEmailVerification(result: unknown): boolean {
  return isEmailVerificationError((result as AuthClientResult | null | undefined)?.error);
}

export const AUTH_COPY = {
  signupVerificationSent: "Check your inbox to confirm your email before signing in.",
  verificationEmailSent: "Verification email sent. Check your inbox for the latest link.",
  passwordResetSent: "If an account exists for that email, a reset link is on its way.",
  passwordResetComplete: "Password reset. Sign in with your new password.",
  missingEmailForVerification: "Enter your email, then resend the verification email.",
  missingResetToken: "This reset link is missing its token. Request a new password reset email.",
  expiredResetToken: "This reset link is invalid or expired. Request a new password reset email.",
  passwordMismatch: "Passwords do not match.",
} as const;

